(() => {
  const MAX_TIMEOUT = 2147483647;
  const restrictionPrefix = 'data-checkout-restriction-';
  const messagePrefix = 'data-checkout-message-';
  const restrictionPriority = ['option', 'message-card', 'single-purchase', 'sale-period'];
  let boundaryTimer;

  const toUnixSeconds = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  };

  const getSaleState = (element, now) => {
    const start = toUnixSeconds(element.dataset.saleStart);
    const end = toUnixSeconds(element.dataset.saleEnd);

    if (element.dataset.comingSoon === 'true') return 'coming_soon';
    if (start > 0 && end > 0 && end <= start) return 'invalid_period';
    if (start > 0 && now < start) return 'before_start';
    if (end > 0 && now >= end) return 'after_end';
    return 'active';
  };

  const getButtonLabel = (state, soldOut) => {
    if (state === 'coming_soon') return 'Coming Soon';
    if (state === 'invalid_period') return '現在販売していません';
    if (state === 'before_start') return '販売開始前';
    if (state === 'after_end') return '販売終了';
    return soldOut ? window.variantStrings?.soldOut || '売り切れ' : window.variantStrings?.addToCart || 'カートに追加する';
  };

  const getSaleMessage = (element, state) => {
    if (state === 'before_start') return element.dataset.saleStartMessage || '';
    if (state === 'active' && toUnixSeconds(element.dataset.saleEnd) > 0) return element.dataset.saleEndMessage || '';
    if (state === 'after_end') return 'この商品の販売は終了しました。';
    if (state === 'invalid_period') return '現在この商品は販売していません。';
    return '';
  };

  const syncProductButton = (button, now) => {
    const state = getSaleState(button, now);
    const soldOut = button.dataset.baseDisabled === 'true';
    const disabled = state !== 'active' || soldOut;
    const sectionId = button.id.replace('ProductSubmitButton-', '');
    const label = button.querySelector(':scope > span');
    const container = button.closest('[data-sale-period-container]');
    const message = container?.querySelector('[data-sale-period-message]');
    const dynamicCheckout = container?.querySelector('[data-sale-period-dynamic-checkout]');
    const optionPickers = Array.from(document.querySelectorAll('[data-sale-period-options]')).filter(
      (picker) => picker.dataset.section === sectionId
    );

    button.dataset.saleState = state;
    if (container) container.dataset.saleState = state;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
    if (label) label.textContent = getButtonLabel(state, soldOut);

    if (message) {
      const text = getSaleMessage(button, state);
      message.textContent = text;
      message.hidden = text === '';
    }

    if (dynamicCheckout) {
      const unavailable = state !== 'active';
      dynamicCheckout.hidden = unavailable;
      dynamicCheckout.setAttribute('aria-hidden', String(unavailable));
    }

    optionPickers.forEach((picker) => {
      const unavailable = state !== 'active';
      picker.dataset.saleState = state;
      picker.hidden = unavailable;
      picker.setAttribute('aria-hidden', String(unavailable));
    });
  };

  window.syncCartCheckoutButton = (button) => {
    if (!button) return;

    const activeRestrictions = Array.from(button.attributes)
      .filter((attribute) => attribute.name.startsWith(restrictionPrefix) && attribute.value === 'true')
      .map((attribute) => attribute.name.slice(restrictionPrefix.length));
    const restriction = restrictionPriority.find((reason) => activeRestrictions.includes(reason)) || activeRestrictions[0];
    const disabled = Boolean(restriction);
    const defaultLabel = button.dataset.checkoutDefaultLabel || 'ご購入手続きへ';
    const message = restriction ? button.getAttribute(`${messagePrefix}${restriction}`) : '';

    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
    button.textContent = message || defaultLabel;
  };

  window.setCartCheckoutRestriction = (button, reason, restricted, message = '') => {
    if (!button) return;
    button.setAttribute(`${restrictionPrefix}${reason}`, String(Boolean(restricted)));
    if (message) button.setAttribute(`${messagePrefix}${reason}`, message);
    window.syncCartCheckoutButton(button);
  };

  const syncCartSalePeriod = (cart, now) => {
    if (!cart) return;

    const itemSource = cart.dataset.saleItemSource;
    const saleItems = itemSource
      ? document.querySelectorAll(itemSource)
      : cart.querySelectorAll('[data-cart-sale-period-item]');
    const restrictedItems = Array.from(saleItems)
      .map((item) => ({ item, state: getSaleState(item, now) }))
      .filter(({ state }) => state !== 'active');
    const buttonSelector = cart.dataset.checkoutButton;
    const button = buttonSelector ? document.querySelector(buttonSelector) : null;
    const alert = cart.querySelector('[data-cart-sale-period-alert]');
    const list = alert?.querySelector('[data-cart-sale-period-list]');

    window.setCartCheckoutRestriction(button, 'sale-period', restrictedItems.length > 0);

    if (!alert || !list) return;

    list.replaceChildren(
      ...restrictedItems.map(({ item, state }) => {
        const listItem = document.createElement('li');
        const title = item.dataset.productTitle || '';
        let reason = '現在購入できません';
        if (state === 'coming_soon') reason = 'Coming Soon';
        if (state === 'before_start') reason = item.dataset.saleStartLabel ? `${item.dataset.saleStartLabel}から販売開始` : '販売開始前';
        if (state === 'after_end') reason = '販売終了';
        if (state === 'invalid_period') reason = '現在販売していません';
        listItem.textContent = `${title}：${reason}`;
        return listItem;
      })
    );
    alert.hidden = restrictedItems.length === 0;
  };

  const scheduleNextBoundary = (now) => {
    const boundaries = Array.from(document.querySelectorAll('[data-sale-start], [data-sale-end]'))
      .flatMap((element) => [toUnixSeconds(element.dataset.saleStart), toUnixSeconds(element.dataset.saleEnd)])
      .filter((boundary) => boundary > now)
      .sort((a, b) => a - b);

    window.clearTimeout(boundaryTimer);
    if (boundaries.length === 0) return;
    boundaryTimer = window.setTimeout(syncSalePeriods, Math.min((boundaries[0] - now) * 1000 + 250, MAX_TIMEOUT));
  };

  function syncSalePeriods() {
    const now = Math.floor(Date.now() / 1000);
    document.querySelectorAll('[data-sale-period-gate]').forEach((button) => syncProductButton(button, now));
    document.querySelectorAll('[data-cart-sale-period]').forEach((cart) => syncCartSalePeriod(cart, now));
    scheduleNextBoundary(now);
  }

  document.addEventListener('DOMContentLoaded', syncSalePeriods);
  document.addEventListener('sale-period:update', syncSalePeriods);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => window.requestAnimationFrame(syncSalePeriods));
    subscribe(PUB_SUB_EVENTS.variantChange, () => window.requestAnimationFrame(syncSalePeriods));
  }

  window.syncSalePeriods = syncSalePeriods;
})();
